import {
  DateField,
  Field,
  getScaleFromIncrement,
  IntegerField,
  NumericField,
  ParameterTypeEnum,
  ParameterUnitType,
  PresenceEnum,
  SelectorField,
  UnifiedLiquidityEnum,
  Unit,
  type OrderStrategy,
  type ParameterLike,
  type PriceProtectionEnum,
  type Security,
} from '@talos/kyoko';

/**
 * Gets the scale for a Price or PriceOffset parameter based on the selected unit type
 * - PriceUnits: uses the provided minPriceIncrement scale
 * - BasisPoints: no scale validation (undefined)
 * - PriceIncrements: scale of 0 (0 decimal places)
 * - Default (no unit specified): uses the minPriceIncrement scale
 * - An unknown unit type will fall back to no validation
 */
export function getScaleForPriceUnit(
  unitValue: string | undefined,
  minPriceIncrement: string | undefined,
  hasUnitsParam: boolean
): number | undefined {
  const priceScale = getScaleFromIncrement(minPriceIncrement);

  // If no units parameter defined, use the default price scale
  if (!hasUnitsParam) {
    return priceScale;
  }

  switch (unitValue) {
    case ParameterUnitType.PriceUnits:
      return priceScale;
    case ParameterUnitType.BasisPoints:
      return undefined; // No scale validation for BPS
    case ParameterUnitType.PriceIncrements:
      return 0; // 0 decimal place for increments
    default:
      return undefined;
  }
}

export const mapParameterToField = (
  parameter: ParameterLike,
  minPriceIncrement: string | undefined,
  quantityScale: number | undefined,
  defaultPriceProtection: PriceProtectionEnum | undefined,
  isModify: boolean | undefined,
  strategy: OrderStrategy | undefined = undefined
) => {
  const isRequired = parameter.Presence === PresenceEnum.Required;
  const placeholder = parameter.Presence === PresenceEnum.Optional ? 'Optional' : '';
  const isVisible =
    parameter.Presence !== PresenceEnum.Hidden &&
    // End price parameters are shown elsewhere and should be excluded from favorites
    !(strategy?.showPriceAsRange && strategy.endPriceParamName === parameter.Name);
  const defaultValue = !isModify ? parameter.DefaultValue : undefined;

  const name = parameter.DisplayName;

  // enum values are only applicable for Enum parameters
  // backend send us values in string format, but expect us to send it back in numeric...
  const enumValues = parameter.EnumValues || [];
  const availableEnums: number[] = enumValues.map(e => e.Index);

  switch (parameter.Type) {
    case ParameterTypeEnum.PriceProtection:
      return new Field({
        name,
        placeholder,
        isRequired,
        value: defaultValue || defaultPriceProtection,
        isVisible,
      }).validate();
    case ParameterTypeEnum.UnifiedLiquidity:
      return new Field({
        name,
        placeholder,
        isRequired,
        value: defaultValue || UnifiedLiquidityEnum.Disabled,
        isVisible,
        // UnifiedLiquidity cannot be changed for existing orders
        isDisabled: isModify,
      }).validate();
    case ParameterTypeEnum.ReduceOnly:
    case ParameterTypeEnum.ReduceFirst:
      return new Field({
        name,
        placeholder,
        isRequired,
        value: defaultValue,
        isVisible,
        isDisabled: isModify,
      }).validate();
    case ParameterTypeEnum.Mode:
      return new Field({
        name,
        placeholder,
        isRequired,
        value: defaultValue,
        isVisible,
      }).validate();
    case ParameterTypeEnum.TargetParticipationRate:
    case ParameterTypeEnum.Percent:
      return new NumericField({
        unit: Unit.Percent,
        placeholder,
        name,
        isRequired,
        value: defaultValue,
        isVisible,
      }).validate();
    case ParameterTypeEnum.Bps:
      return new NumericField({
        unit: Unit.Bps,
        placeholder,
        name,
        scale: 2,
        isRequired,
        value: defaultValue,
        isVisible,
      }).validate();
    case ParameterTypeEnum.Integer:
      return new IntegerField({
        placeholder,
        name,
        isRequired,
        value: typeof defaultValue === 'string' ? parseInt(defaultValue) : defaultValue,
        isVisible,
      }).validate();
    case ParameterTypeEnum.Qty:
      return new NumericField({
        placeholder,
        name,
        scale: quantityScale,
        isRequired,
        value: defaultValue,
        isVisible,
      }).validate();
    case ParameterTypeEnum.Price:
    case ParameterTypeEnum.PriceOffset: {
      // Check if this parameter has a units parameter defined
      // The strategy.Parameters are full Parameter class instances with unitsParam getter
      const fullParameter = strategy?.Parameters.find(p => p.Name === parameter.Name);
      const unitsParamName = fullParameter?.unitsParam;
      const hasUnitsParam = !!unitsParamName;

      // Get the default unit value if units parameter exists
      let defaultUnit: string | undefined;
      if (unitsParamName && strategy) {
        const unitsParameter = strategy.Parameters.find(p => p.Name === unitsParamName);
        defaultUnit = unitsParameter?.DefaultValue ?? unitsParameter?.options?.[0]?.name;
      }

      const scale = getScaleForPriceUnit(defaultUnit, minPriceIncrement, hasUnitsParam);

      return new NumericField({
        name,
        placeholder,
        scale,
        isRequired,
        value: defaultValue,
        isVisible,
      }).validate();
    }
    case ParameterTypeEnum.Date:
      return new DateField({ name, placeholder, isRequired, value: defaultValue, isVisible }).validate();
    case ParameterTypeEnum.Enum:
      return new SelectorField<number>({
        name,
        placeholder,
        isRequired,
        value: Number(defaultValue),
        isVisible,
        availableItems: availableEnums,
      }).validate();
    case ParameterTypeEnum.Symbol:
      return new Field<Security>({ name, placeholder, isRequired, isVisible }).validate();
    case ParameterTypeEnum.HedgeStrategy:
      return new Field<OrderStrategy>({ name, placeholder, isRequired, isVisible }).validate();
    default:
      return new Field({ name, placeholder, isRequired, value: defaultValue, isVisible }).validate();
  }
};
